import React from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const ProductSelectionStep = ({
  inputValue,
  onInputChange,
  searchResults,
  loading,
  selectedProducts,
  onSelectProduct,
  onRemoveProduct,
  onNext,
}) => {
  const selectedProductIds = new Set(
    selectedProducts.map((p) => p.productFormatId)
  );

  // Filter out already selected products from search results
  const availableOptions = searchResults.filter(
    (result) => !selectedProductIds.has(result.productFormatId)
  );

  return (
    <>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="body1" sx={{ fontSize: { xs: 16, sm: 16, md: 18 } }}>
          Search Product Formats
        </Typography>
      </Box>
      <Box sx={{ marginBottom: 4, marginTop: 2 }}>
        <Autocomplete
          options={availableOptions}
          getOptionLabel={(option) => option.displayName || ""}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            onInputChange({ target: { value: newInputValue } });
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              onSelectProduct(newValue);
            }
          }}
          loading={loading}
          noOptionsText={inputValue ? "No products found" : "Start typing to search"}
          value={null}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex !important",
                justifyContent: "space-between !important",
                alignItems: "center !important",
                gap: 2,
              }}
            >
              <Typography variant="body1" sx={{ flex: 1 }}>
                {option.displayName}
              </Typography>
              <AddCircleOutlineIcon fontSize="small" sx={{ flexShrink: 0 }} />
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search product formats"
              sx={{
                "& legend": { display: "none" },
                "& fieldset": { top: 0 },
                "& .MuiOutlinedInput-root": {
                  background: (theme) => theme.palette.pure.white,
                },
                "& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    display: "none",
                  },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start" sx={{ marginLeft: -0.5 }}>
                      <SearchIcon />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      {/* Selected Products */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 600 }}>
          Products To Add ({selectedProducts.length})
        </Typography>
        <Box
          sx={{
            minHeight: "300px",
            maxHeight: "300px",
            overflow: "auto",
            backgroundColor: (theme) => theme.palette.terrain[300],
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {selectedProducts.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Typography variant="body1" fontWeight={600}>
                No Products Selected Yet...
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {selectedProducts.map((product) => (
                <ListItem
                  key={product.productFormatId}
                  sx={{
                    backgroundColor: (theme) => theme.palette.terrain[50],
                    borderBottom: "1px solid",
                    borderColor: (theme) => theme.palette.terrain[300],
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 2,
                  }}
                >
                  <Typography variant="body1">
                    {product.displayName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveProduct(product.productFormatId)}
                    color="error"
                  >
                    <CloseIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Next Button */}
      <Box sx={{ marginTop: 4 }}>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={selectedProducts.length === 0}
          fullWidth
          sx={{ height: 48 }}
        >
          Next Step
        </Button>
      </Box>
    </>
  );
};

export default ProductSelectionStep;
